/**
 * Migration script to create the homepage document from existing collections
 * 
 * Run this script once to migrate your existing Firestore data to the new homepage structure
 * 
 * Usage:
 * 1. Import this function in your admin dashboard or run it in browser console
 * 2. Call: await migrateToHomepageDocument()
 * 3. Verify the document was created at /homepage/data in Firebase Console
 */

import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db, isFirebaseInitialized } from '../firebase/config-v9';

/**
 * Migrate all homepage data to a single document
 */
export async function migrateToHomepageDocument() {
  if (!isFirebaseInitialized() || !db) {
    console.error('❌ Firebase not initialized');
    return { success: false, error: 'Firebase not initialized' };
  }

  try {
    console.log('🔄 Starting migration to homepage document...');

    // Fetch all data from existing collections in parallel
    const [
      productsSnapshot,
      categoriesSnapshot,
      couponsSnapshot,
      announcementsSnapshot,
      videosSnapshot
    ] = await Promise.all([
      // Products - get all products
      getDocs(collection(db, 'products')),
      
      // Categories - get active categories ordered by displayOrder
      getDocs(
        query(
          collection(db, 'categories'),
          where('isActive', '==', true),
          orderBy('displayOrder', 'asc')
        )
      ),
      
      // Coupons - get active coupons
      getDocs(
        query(
          collection(db, 'coupons'),
          where('isActive', '==', true)
        )
      ),
      
      // Announcements - get active announcements
      getDocs(
        query(
          collection(db, 'announcements'),
          where('isActive', '==', true)
        )
      ),
      
      // Founder videos - get all videos ordered by order
      getDocs(
        query(
          collection(db, 'founderVideos'),
          orderBy('order', 'asc')
        )
      )
    ]);

    // Transform snapshots to arrays/objects
    const products = productsSnapshot.docs.map(doc => {
      const data = doc.data();
      const { id: customId, ...dataWithoutId } = data;
      return {
        id: doc.id,
        ...dataWithoutId,
        customId: customId || null
      };
    });

    const categories = categoriesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const coupons = {};
    couponsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      coupons[data.code] = {
        id: doc.id,
        ...data
      };
    });

    // Filter announcements by date and sort by priority
    const now = new Date();
    const announcements = announcementsSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(announcement => {
        const startDate = announcement.startDate?.toDate?.() || new Date(announcement.startDate || 0);
        const endDate = announcement.endDate?.toDate?.() || new Date(announcement.endDate || 9999999999999);
        return (!announcement.startDate || now >= startDate) && 
               (!announcement.endDate || now <= endDate);
      })
      .sort((a, b) => (b.priority || 1) - (a.priority || 1));

    const founderVideos = videosSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Create homepage document structure
    const homepageData = {
      products,
      categories,
      coupons,
      announcements,
      founderVideos,
      banners: [], // Add banners if you have a banners collection
      lastUpdated: serverTimestamp(),
      version: 1
    };

    // Save to Firestore
    await setDoc(doc(db, 'homepage', 'data'), homepageData);

    console.log('✅ Homepage document created successfully!');
    console.log(`   - Products: ${products.length}`);
    console.log(`   - Categories: ${categories.length}`);
    console.log(`   - Coupons: ${Object.keys(coupons).length}`);
    console.log(`   - Announcements: ${announcements.length}`);
    console.log(`   - Founder Videos: ${founderVideos.length}`);

    return {
      success: true,
      data: {
        products: products.length,
        categories: categories.length,
        coupons: Object.keys(coupons).length,
        announcements: announcements.length,
        founderVideos: founderVideos.length
      }
    };
  } catch (error) {
    console.error('❌ Error creating homepage document:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Update the homepage document with latest data
 * Call this whenever source collections are updated
 */
export async function updateHomepageDocument() {
  return migrateToHomepageDocument();
}











